import { DatePipe } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatSnackBar } from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';
import { QuestionnaireRequest } from 'src/API';
import { Assessment } from 'src/app/core/api/assessment.service';
import { PatientService } from 'src/app/core/api/patient.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { ConfirmModalService } from 'src/app/shared/confirm-modal/confirm-modal.service';
import { LoadingSpinnerService } from 'src/app/shared/loading-spinner/loading-spinner.service';
import { QuestionnaireScorePipe } from 'src/app/shared/shared-pipes/questionnaire-score.pipe';
import { QuestionnaireResponseModalComponent } from '../../../questionnaire-response-modal/questionnaire-response-modal.component';
import { QuestionnaireRequestService } from './../../../../../core/api/questionnaire-request.service';
import {
  AssessmentLinkContext,
  LinkQuestionnaireTableBuilder,
  QuestionnaireRequestsByType
} from './link-questionnaire-table.builder';
import { LinkQuestionnaireService } from './link-questionnaire.service';

@Component({
  selector: 'csi-link-assessment-to-questionnaire-modal',
  templateUrl: './link-assessment-to-questionnaire-modal.component.html',
  styleUrls: ['./link-assessment-to-questionnaire-modal.component.css'],
  providers: [QuestionnaireScorePipe]
})
export class LinkQuestionnaireModalComponent implements OnInit {
  @HostListener('window:keyup.esc') onKeyUp() {
    this.dialogRef.close(this.newlyLinkedQuestionnaires);
  }
  public saveFn: () => Observable<Assessment>;
  public questionnaireRequests: QuestionnaireRequest[];

  public assessment: Assessment;
  public assessmentId: string;
  public currentDateString: string;

  public linking = false;
  public notEmpty = false;

  public questionnaireRequestsByType: QuestionnaireRequestsByType = {};

  public newlyLinkedQuestionnaires: QuestionnaireRequest[] = [];
  public newlyLinkedQuestionnaireIds: string[] = [];

  public readonly questionnaireHeaderMap = {
    createdAt: 'Sent On',
    completedAt: 'Completed At',
    assessment: 'Linked Assessment Date',
    score: 'Score'
  };

  public readonly questionnaireHeaderKeysWithoutActionKey = Object.keys(
    this.questionnaireHeaderMap
  );

  public readonly questionnaireHeaderKeysWithActionsKey = [
    ...this.questionnaireHeaderKeysWithoutActionKey,
    'actions'
  ];

  private readonly tableBuilder = new LinkQuestionnaireTableBuilder();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    private dialogRef: MatDialogRef<LinkQuestionnaireModalComponent>,
    private dialog: MatDialog,
    public patientService: PatientService,
    public appStateService: AppStateService,
    public questionnaireRequestService: QuestionnaireRequestService,
    public loadingSpinnerService: LoadingSpinnerService,
    public confirmModalService: ConfirmModalService,
    public datePipe: DatePipe,
    private translocoService: TranslocoService,
    private linkQuestionnaireService: LinkQuestionnaireService,
    private snackBar: MatSnackBar
  ) {
    this.saveFn = data.saveFn;

    const pathname = location.pathname;
    this.assessmentId = pathname.substring(pathname.indexOf(':') + 1, pathname.length - 1);
    const assessments = this.patientService.assessments || [];
    this.assessment =
      this.assessmentId !== 'new' &&
      assessments.find(assessment => assessment.id === this.assessmentId);

    // Falls back to today when there is no resolved assessment ('new', or an id not present in the
    // patient's assessments) — guarding against a null/undefined list and a missing match.
    this.currentDateString = this.assessment
      ? this.datePipe.transform(this.assessment.createdAt)
      : this.datePipe.transform(new Date());
  }

  ngOnInit() {
    const currentPatient = this.appStateService.getCurrentPatient();
    if (currentPatient && currentPatient.questionnaireRequests) {
      this.questionnaireRequests = currentPatient.questionnaireRequests.items;
      const { questionnaireRequestsByType, notEmpty } = this.tableBuilder.buildTable(
        this.questionnaireRequests,
        this.buildContext()
      );
      this.questionnaireRequestsByType = questionnaireRequestsByType;
      this.notEmpty = notEmpty;
    }

    this.dialogRef.backdropClick().subscribe(() => {
      if (this.linking) return;
      this.dialogRef.close(this.newlyLinkedQuestionnaires);
    });
  }

  showQuestionnaireResponse(questionnaireRequest: QuestionnaireRequest) {
    QuestionnaireResponseModalComponent.open(
      this.dialog,
      questionnaireRequest,
      null,
      this.patientService.patient.id
    );
  }

  onLink(questionnaire: QuestionnaireRequest) {
    let confirmButtonLabel = this.translocoService.translate('Link');
    if (this.assessmentId === 'new') {
      confirmButtonLabel = this.translocoService.translate('Create Assessment & Link');
    }

    const cancelButtonLabel = this.translocoService.translate('Cancel');
    const linkingToText = this.translocoService.translate('Linking to');
    const completedOnText = this.translocoService.translate('completed on');
    const cannotUnlinkText = this.translocoService.translate(
      'Once linked to this questionnaire, you cannot unlink. Do you still wish to continue?'
    );

    const questionnaireSpecificMessage = this.translocoService.translate(
      'This may overwrite the corresponding Medical History and Symptom fields in the assessment.'
    );

    const questionnaireName = this.questionnaireRequestService.getQuestionnaireFriendlyName(
      questionnaire
    );
    const completedDate = this.datePipe.transform(questionnaire.completedAt);

    const body = `${linkingToText} ${questionnaireName} ${completedOnText} ${completedDate}. ${questionnaireSpecificMessage} ${cannotUnlinkText}`;

    this.confirmModalService
      .show(
        this.translocoService.translate('Link Questionnaire'),
        body,
        confirmButtonLabel,
        cancelButtonLabel
      )
      .subscribe(continueLinking => {
        if (continueLinking) {
          this.linkQuestionnaire(questionnaire);
        }
      });
  }

  private linkQuestionnaire(questionnaire: QuestionnaireRequest) {
    this.loadingSpinnerService.show();

    const questionnaireType = this.tableBuilder.resolveQuestionnaireType(questionnaire);
    const previousBucket = this.questionnaireRequestsByType[questionnaireType];
    const selectedQuestionnaire = previousBucket.find(
      patientQuestionnaire => patientQuestionnaire.id === questionnaire.id
    );
    const previousAssessment = selectedQuestionnaire.assessment;

    // Optimistically show the questionnaire as linked to the current assessment.
    selectedQuestionnaire.assessment = {
      __typename: 'Assessment',
      createdAt: new Date().toDateString()
    };

    this.linking = true;
    this.linkQuestionnaireService
      .link(questionnaire, {
        assessmentId: this.assessmentId,
        assessment: this.assessment,
        saveFn: this.saveFn
      })
      .subscribe({
        next: outcome => {
          this.assessment = outcome.assessment;
          this.assessmentId = outcome.assessment.id;
          this.newlyLinkedQuestionnaires.push(outcome.linkedQuestionnaire);
          this.newlyLinkedQuestionnaireIds.push(outcome.linkedQuestionnaire.id);
          this.linking = false;
        },
        error: () => {
          // Roll back the optimistic link so the row does not falsely show as linked.
          selectedQuestionnaire.assessment = previousAssessment;
          this.questionnaireRequestsByType[questionnaireType] = previousBucket;
          this.linking = false;
          this.snackBar.open(
            this.translocoService.translate('Linking failed. Please try again.'),
            this.translocoService.translate('Dismiss'),
            { duration: 7000 }
          );
        }
      });

    this.questionnaireRequestsByType[questionnaireType] = this.tableBuilder.filterStartingFromDate(
      questionnaire.completedAt,
      previousBucket
    );

    this.loadingSpinnerService.hide();
  }

  private buildContext(): AssessmentLinkContext {
    return {
      assessmentId: this.assessmentId,
      assessmentCreatedAt: this.assessment ? this.assessment.createdAt : null
    };
  }
}
